import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    Stack,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { proxyFetch } from './useLiveEvents';
import { bewebItem, catColor, dateClock } from '../../components/parking/eventItems';
import { looksLikePlate, matchPlate, normalizePlate } from './plateMatch';
import { COLOR_IN, COLOR_OUT } from './Timeline';

export const DRAWER_WIDTH = 440;

// image blobs already loaded in this tab (object URLs), so re-opening is instant
const imageCache = new Map();

export function DirLabel({ dir }) {
    return (
        <Stack direction="row" spacing={0.75} alignItems="center" component="span">
            <Box
                component="span"
                sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: dir === 'in' ? COLOR_IN : COLOR_OUT,
                }}
            />
            <span>{dir === 'in' ? 'Be' : 'Ki'}</span>
        </Stack>
    );
}

// plate OCR in a worker (public/ocr-worker.js); the model is downloaded on first use
let worker = null;
let ocrSeq = 0;

function useOcr(event, blob) {
    const [state, setState] = useState(null);
    useEffect(() => setState(null), [event?.id]);

    const run = () => {
        if (!blob) return;
        const id = ++ocrSeq;
        if (!worker) worker = new Worker('/ocr-worker.js', { type: 'module' });
        const files = {};
        setState({
            busy: true,
            text: 'Modell betöltése… (első alkalommal ~300 MB, utána gyorsítótárból)',
        });
        worker.onmessage = ({ data }) => {
            if (data.type === 'progress') {
                files[data.file] = data;
                const all = Object.values(files);
                const loaded = all.reduce((a, f) => a + (f.loaded || 0), 0);
                const total = all.reduce((a, f) => a + (f.total || 0), 0);
                setState({
                    busy: true,
                    text: `Modell letöltése… ${total ? Math.round((loaded / total) * 100) : 0}%`,
                });
            } else if (data.type === 'ready') {
                setState({ busy: true, text: `Felismerés folyamatban (${data.device})…` });
            } else if (data.id === id) {
                if (data.type === 'error') setState({ error: data.error });
                else {
                    const regions = data.regions.map((r) => ({
                        ...r,
                        plate: looksLikePlate(r.text),
                        ...matchPlate(r.text, event.plate),
                    }));
                    setState({ result: { ...data, regions } });
                }
            }
        };
        worker.postMessage({
            id,
            blob,
            device: new URLSearchParams(window.location.search).get('ocr'),
        });
    };
    return [state, run];
}

function Photo({ event, onBlob }) {
    const [state, setState] = useState({});
    const [zoom, setZoom] = useState(false);
    const current = useRef(null);
    current.current = event.id;

    useEffect(() => {
        const cached = imageCache.get(event.id);
        setState(cached ? { url: cached.url } : {});
        onBlob(cached ? cached.blob : null);
    }, [event.id]);

    const load = async () => {
        setState({ loading: true });
        try {
            const blob = await (await proxyFetch(`/image/${event.id}`)).blob();
            const url = URL.createObjectURL(blob);
            imageCache.set(event.id, { blob, url });
            if (current.current !== event.id) return;
            setState({ url });
            onBlob(blob);
        } catch (e) {
            if (current.current === event.id)
                setState({ error: e.status === 404 ? 'Nincs kép ehhez az eseményhez' : e.message });
        }
    };

    return (
        <Box
            sx={{
                position: 'relative',
                aspectRatio: '16 / 9',
                bgcolor: 'action.hover',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            {state.url && (
                <>
                    <Box
                        component="img"
                        src={state.url}
                        alt={event.plate}
                        onClick={() => setZoom(true)}
                        sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            bgcolor: '#000',
                            cursor: 'zoom-in',
                        }}
                    />
                    <Dialog open={zoom} onClose={() => setZoom(false)} maxWidth="xl">
                        <Box
                            component="img"
                            src={state.url}
                            alt={event.plate}
                            onClick={() => setZoom(false)}
                            sx={{ maxWidth: '96vw', maxHeight: '92vh', display: 'block' }}
                        />
                    </Dialog>
                </>
            )}
            {!state.url && !state.loading && !state.error && (
                <Button variant="outlined" startIcon={<PhotoCameraIcon />} onClick={load}>
                    Kép betöltése
                </Button>
            )}
            {state.loading && (
                <Stack alignItems="center" spacing={1}>
                    <CircularProgress size={24} />
                    <Typography variant="caption" color="text.secondary">
                        Kép lekérése a parkoló rendszerből…
                    </Typography>
                </Stack>
            )}
            {state.error && (
                <Typography variant="body2" color="text.secondary">
                    {state.error}
                </Typography>
            )}
        </Box>
    );
}

function OcrOverlay({ result }) {
    const pts = (b) => [0, 2, 4, 6].map((i) => `${b[i]},${b[i + 1]}`).join(' ');
    return (
        <svg
            viewBox={`0 0 ${result.width} ${result.height}`}
            preserveAspectRatio="xMidYMid meet"
            style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
            }}
        >
            {result.regions.map((r) => (
                <polygon
                    key={pts(r.box)}
                    points={pts(r.box)}
                    fill="none"
                    stroke={r.plate ? '#3ddc84' : '#ffd400'}
                    strokeWidth={r.plate ? 6 : 4}
                />
            ))}
        </svg>
    );
}

export default function PlateDrawer({
    plate,
    eventId,
    events,
    onSelectEvent,
    onClose,
    onSearch,
    onOpenParking,
}) {
    const [blob, setBlob] = useState(null);
    // every event of the plate (entries/exits, payments, rejections, errors), newest first
    const history = plate
        ? [...events.values()]
              .filter((e) => e.plate === plate)
              .sort((a, b) => b.ts - a.ts)
              .map((e) => ({ ...e, item: bewebItem(e) }))
        : [];
    const selectedEvent = history.find((e) => e.k === eventId) || history[0];
    // the camera image belongs to the passage: the selected one, or the passage closest to the selected event
    const moves = history.filter((e) => e.type === 'ID moved');
    const current =
        selectedEvent && selectedEvent.type !== 'ID moved' && moves.length
            ? moves.reduce((best, e) =>
                  Math.abs(e.ts - selectedEvent.ts) < Math.abs(best.ts - selectedEvent.ts)
                      ? e
                      : best
              )
            : selectedEvent;
    const [ocr, runOcr] = useOcr(current, blob);
    const ins = history.filter((e) => e.dir === 'in').length;
    const outs = history.filter((e) => e.dir === 'out').length;
    const fails = history.filter((e) => e.item.sev === 'fail').length;
    const cands = ocr?.result
        ? ocr.result.regions.filter((r) => r.plate).sort((a, b) => b.rank - a.rank)
        : [];
    const others = ocr?.result ? ocr.result.regions.filter((r) => !r.plate) : [];

    return (
        <Drawer
            anchor="right"
            variant="persistent"
            open={!!current}
            PaperProps={{
                sx: {
                    width: { xs: '100%', sm: DRAWER_WIDTH },
                    top: 64,
                    height: 'calc(100% - 64px)',
                },
            }}
        >
            {current && (
                <>
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ px: 2, py: 1.5 }}
                    >
                        <Typography
                            variant="h5"
                            sx={{ fontFamily: 'monospace', fontWeight: 700, letterSpacing: 1 }}
                        >
                            {current.plate}
                        </Typography>
                        <Stack direction="row" spacing={0.5}>
                            <IconButton
                                onClick={() => onOpenParking(current.plate)}
                                title="Parkolás megnyitása (idővonal)"
                            >
                                <OpenInNewIcon />
                            </IconButton>
                            <IconButton onClick={onClose} title="Bezárás">
                                <CloseIcon />
                            </IconButton>
                        </Stack>
                    </Stack>
                    <Box sx={{ position: 'relative' }}>
                        {current.type === 'ID moved' ? (
                            <Photo event={current} onBlob={setBlob} />
                        ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                                Ehhez az eseményhez nincs kamerakép.
                            </Typography>
                        )}
                        {ocr?.result && <OcrOverlay result={ocr.result} />}
                    </Box>
                    {blob && (
                        <Box sx={{ px: 2, py: 1, display: 'grid', gap: 1 }}>
                            {!ocr && (
                                <Button
                                    size="small"
                                    startIcon={<DocumentScannerIcon />}
                                    onClick={runOcr}
                                    sx={{ justifySelf: 'start' }}
                                >
                                    Rendszám felismerés (böngészőben)
                                </Button>
                            )}
                            {ocr?.busy && (
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <CircularProgress size={16} />
                                    <Typography variant="body2" color="text.secondary">
                                        {ocr.text}
                                    </Typography>
                                </Stack>
                            )}
                            {ocr?.error && (
                                <Typography variant="body2" color="error">
                                    Hiba: {ocr.error}{' '}
                                    <Button size="small" onClick={runOcr}>
                                        Újra
                                    </Button>
                                </Typography>
                            )}
                            {ocr?.result && (
                                <>
                                    <Typography variant="body2">
                                        {cands.length ? (
                                            <>
                                                Ellenőrzés: rögzített <b>{current.plate}</b>,
                                                felismert <b>{cands[0].text}</b> –{' '}
                                                <Chip
                                                    size="small"
                                                    label={`${Math.round(
                                                        cands[0].score * 100
                                                    )}% egyezés`}
                                                    color={
                                                        cands[0].score >= 0.85
                                                            ? 'success'
                                                            : 'warning'
                                                    }
                                                />
                                            </>
                                        ) : (
                                            'Nem találtam rendszámnak látszó szöveget a képen.'
                                        )}
                                    </Typography>
                                    {cands.length > 0 && (
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            flexWrap="wrap"
                                            useFlexGap
                                        >
                                            {cands.map((c) => (
                                                <Chip
                                                    key={c.text + c.box.join()}
                                                    size="small"
                                                    variant="outlined"
                                                    label={`${c.text} · ${Math.round(
                                                        c.score * 100
                                                    )}%`}
                                                    onClick={() => onSearch(normalizePlate(c.text))}
                                                    title="Keresés erre a rendszámra"
                                                    sx={{ fontFamily: 'monospace' }}
                                                />
                                            ))}
                                        </Stack>
                                    )}
                                    {others.length > 0 && (
                                        <Typography variant="caption" color="text.secondary">
                                            Egyéb szöveg: {others.map((r) => r.text).join(' · ')}
                                        </Typography>
                                    )}
                                    <Typography variant="caption" color="text.secondary">
                                        {ocr.result.ms} ms ·{' '}
                                        <Button size="small" onClick={runOcr}>
                                            Újra
                                        </Button>
                                    </Typography>
                                </>
                            )}
                        </Box>
                    )}
                    <Divider />
                    <Box sx={{ px: 2, py: 1 }}>
                        <Typography variant="body2" component="div">
                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                flexWrap="wrap"
                                useFlexGap
                            >
                                <b>{selectedEvent.item.label}</b>
                                <span>
                                    · {selectedEvent.time} · {selectedEvent.item.gateLabel}
                                </span>
                            </Stack>
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {current.kind || 'Ismeretlen típus'} · {ins} behajtás, {outs} kihajtás,{' '}
                            {fails} hiba
                        </Typography>
                    </Box>
                    <Divider />
                    <List dense sx={{ overflowY: 'auto', flex: 1 }}>
                        {history
                            .filter((e) => !e.item.noise)
                            .map((e) => (
                                <ListItemButton
                                    key={e.k}
                                    selected={e.k === selectedEvent.k}
                                    onClick={() => onSelectEvent(e.k)}
                                >
                                    <ListItemText
                                        primary={
                                            <Stack
                                                direction="row"
                                                spacing={1.5}
                                                alignItems="center"
                                            >
                                                <Box
                                                    sx={{
                                                        width: 8,
                                                        height: 8,
                                                        flexShrink: 0,
                                                        borderRadius:
                                                            e.item.sev === 'fail' ? 0 : '50%',
                                                        transform:
                                                            e.item.sev === 'fail'
                                                                ? 'rotate(45deg)'
                                                                : 'none',
                                                        bgcolor: catColor(e.item.cat),
                                                    }}
                                                />
                                                <span>{dateClock(e.ts)}</span>
                                                <Typography
                                                    variant="body2"
                                                    component="span"
                                                    color={
                                                        e.item.sev === 'fail'
                                                            ? 'error.main'
                                                            : 'text.primary'
                                                    }
                                                    sx={{
                                                        fontWeight:
                                                            e.item.sev === 'info' ? 400 : 700,
                                                    }}
                                                >
                                                    {e.item.label}
                                                </Typography>
                                            </Stack>
                                        }
                                        secondary={[
                                            e.item.gateLabel,
                                            e.amount ? `${e.amount} Ft` : '',
                                            e.user,
                                        ]
                                            .filter(Boolean)
                                            .join(' · ')}
                                    />
                                </ListItemButton>
                            ))}
                    </List>
                </>
            )}
        </Drawer>
    );
}
